import { useNotifyStatus } from "contexts/status";
import { useFormatLocalDate } from "hooks/useFormatLocalDate";
import { useCallback } from "react";
import {
  AutoRecipeCreate,
  SessionCreate,
  useCreateAutoRecipeMutation,
  useCreateSessionMutation,
} from "src/store/api/generatedApi";
import { useTenantTranslation } from "./formatters";
import { useTenant } from "./settings";
import { useNavigateTo } from "./useNavigateTo";

export const useNavigateToTenantHome = () => {
  const navigateTo = useNavigateTo();
  const navigateToTenantHome = useCallback(
    (tenant: string) => {
      navigateTo({ location: `/${tenant}` });
    },
    [navigateTo]
  );

  return navigateToTenantHome;
};

export const useNavigateToHome = () => {
  const navigateTo = useNavigateTo();
  const tenant = useTenant();
  const navigateToHome = useCallback(() => {
    navigateTo({ location: `/${tenant}` });
  }, [navigateTo, tenant]);

  return navigateToHome;
};

export const useNavigateToNewBlankSearch = () => {
  const tenant = useTenant();
  const [createSession, createSessionMutation] = useCreateSessionMutation();
  const navigate = useNavigateToSearch();
  const formatDate = useFormatLocalDate("datetime");
  const notifyStatus = useNotifyStatus();
  const { t } = useTenantTranslation();
  const errorMessage = t("unknownError");
  const navigateToNewBlankSearch = useCallback(() => {
    const sessionCreate: SessionCreate = {
      name: formatDate(new Date()),
      search_type: "charging",
    };
    return createSession({
      tenantName: tenant,
      sessionCreate: sessionCreate,
    })
      .unwrap()
      .then(({ id }) => {
        navigate(id);
      })
      .catch(() => {
        notifyStatus({ type: "error", text: errorMessage });
      });
  }, [tenant, formatDate, createSession, navigate, notifyStatus, errorMessage]);

  return [navigateToNewBlankSearch, createSessionMutation] as const;
};

export const useNavigateToNewAutoSearch = () => {
  const tenant = useTenant();
  const [createSearch, createAutoRecipeMutation] =
    useCreateAutoRecipeMutation();
  const navigate = useNavigateToSearch();
  const navigateToNewAutoSearch = useCallback(
    (request: AutoRecipeCreate) =>
      createSearch({ tenantName: tenant, autoRecipeCreate: request })
        .unwrap()
        .then(({ id }) => {
          navigate(id);
        }),
    [tenant, createSearch, navigate]
  );

  return [navigateToNewAutoSearch, createAutoRecipeMutation] as const;
};

export const useNavigateToSearch = () => {
  const tenant = useTenant();
  const navigateTo = useNavigateTo();
  const navigateToSearch = useCallback(
    (searchId: number, period: number | undefined = 1) => {
      navigateTo({
        location: `/${tenant}/search`,
        params: {
          search: searchId.toString(),
          plan: null,
          period: period.toString(),
        },
      });
    },
    [navigateTo, tenant]
  );

  return navigateToSearch;
};

export const useNavigateToDebugSearch = () => {
  const tenant = useTenant();
  const navigateTo = useNavigateTo();
  const navigateToDebugSearch = useCallback(
    (searchId: number) => {
      navigateTo({
        location: `/${tenant}/debug-search`,
        params: { search: searchId.toString(), plan: null },
      });
    },
    [navigateTo, tenant]
  );

  return navigateToDebugSearch;
};

export const useNavigateToPlan = () => {
  const tenant = useTenant();
  const navigateTo = useNavigateTo();
  const navigateToPlan = useCallback(
    (planId: number, searchId: number, period: number | undefined = 1) => {
      navigateTo({
        location: `/${tenant}/plan`,
        params: {
          plan: planId.toString(),
          search: searchId.toString(),
          period: period.toString(),
        },
      });
    },
    [navigateTo, tenant]
  );

  return navigateToPlan;
};

export const useNavigateToSearchPlanPeek = () => {
  const tenant = useTenant();
  const navigateTo = useNavigateTo();
  const navigateToSearchPlanPeek = useCallback(
    (searchId: number, planId: number) => {
      navigateTo({
        location: `/${tenant}/search`,
        params: { search: searchId.toString(), plan: planId.toString() },
      });
    },
    [navigateTo, tenant]
  );

  return navigateToSearchPlanPeek;
};

export const useNavigateToReviewRecentHeats = () => {
  const tenant = useTenant();
  const navigateTo = useNavigateTo();
  const navigateToReviewRecentHeats = useCallback(() => {
    navigateTo({ location: `/${tenant}/live/review-recent-heats` });
  }, [navigateTo, tenant]);

  return navigateToReviewRecentHeats;
};

export const useNavigateToLiveBuckets = () => {
  const tenant = useTenant();
  const navigateTo = useNavigateTo();
  const navigateToLiveBuckets = useCallback(() => {
    navigateTo({ location: `/${tenant}/live/buckets` });
  }, [navigateTo, tenant]);

  return navigateToLiveBuckets;
};
